import { Injectable } from '@angular/core';
import * as bcrypt from 'bcryptjs';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { sha256Hex } from '../helpers';
import { ApiService } from './api.service';

/** What a password re-check sends instead of the password. */
export interface ReauthProof {
  challengeId: string;
  proof: string;
}

interface ReauthChallenge {
  salt: string;
  nonce: string;
  challengeId: string;
  /** True when the password being checked is the SSO one. */
  sso?: boolean;
}

/**
 * Re-checking the signed-in user's password — leaving monitoring mode,
 * confirming a credential action.
 *
 * The password never leaves the browser: the server hands out the salt of the
 * stored hash and a single-use nonce, and gets `sha256(hash:nonce)` back, as on
 * the sign-in page. When SSO is on, the challenge is SSO's, so the password
 * proven is the one the user signs in with there — not this lab's old copy.
 */
@Injectable({ providedIn: 'root' })
export class ReauthService extends ApiService {
  public prove(password: string): Observable<ReauthProof> {
    return this.request({ path: 'users/reauth-challenge', method: 'GET' }).pipe(
      switchMap((challenge: ReauthChallenge) => from(proofFor(password, challenge))),
    );
  }
}

async function proofFor(password: string, challenge: ReauthChallenge): Promise<ReauthProof> {
  const hash = await bcrypt.hash(password, challenge.salt);
  return {
    challengeId: challenge.challengeId,
    proof: await sha256Hex(`${hash}:${challenge.nonce}`),
  };
}

/**
 * The message for a re-check the server refused for a reason other than a
 * mistyped password, or null when it was a plain mismatch (shown on the field).
 */
export function reauthRefusalMessage(code: string | undefined | null): string | null {
  switch (code) {
    case 'AUTHORIZATION.ACCOUNT_LOCKED':
      return 'Your account is locked after too many attempts. Check your email for the unlock link, or try again later.';
    case 'AUTHORIZATION.PASSWORD_EXPIRED':
      return 'Your password has expired. We have emailed you a link to set a new one.';
    case 'AUTHORIZATION.SSO_UNAVAILABLE':
      return 'Your password could not be checked right now. Nothing was changed — please try again.';
    case 'AUTHORIZATION.REAUTH_PROOF_REQUIRED':
      return 'Please reload the page and try again.';
    default:
      return null;
  }
}
